/**
 * BuilderEngine — the runtime entry point. Given a validated SiteSchema and a
 * SectionRegistry, it resolves the theme + asset resolver once, publishes the renderer
 * context, and renders pages in order. Navbar is now a section (no separate navigation
 * renderer). It knows nothing about AI, providers, the backend, or business rules — it
 * only renders. Theme Resolver + Asset Resolver are the preserved V1 implementations.
 * See BUILDER_ENGINE_V2.md.
 */
import { useMemo } from 'react'
import type { SiteSchema } from '../schema'
import { resolveTheme } from './theme/themeResolver'
import { createAssetResolver } from './assets/assetResolver'
import { RendererContext } from './rendererContext'
import type { RendererContextValue } from './rendererContext'
import { PageRenderer } from './PageRenderer'
import type { SectionRegistry, RuntimeConfig } from './types'

export interface BuilderEngineProps {
  /** An already-validated (and, if needed, V1→V2 migrated) Site Schema V2. */
  readonly schema: SiteSchema
  /** The section registry mapping kind → RegistryEntry. */
  readonly registry: SectionRegistry
  /** Optional observability config (onError hook). */
  readonly config?: RuntimeConfig
}

export function BuilderEngine({ schema, registry, config }: BuilderEngineProps) {
  const value = useMemo<RendererContextValue>(
    () => ({
      theme: resolveTheme(schema.theme, schema.branding),
      locale: schema.locale,
      assets: createAssetResolver(schema.assets),
      registry,
      config: config ?? {},
    }),
    [schema, registry, config],
  )

  return (
    <RendererContext.Provider value={value}>
      {schema.pages.map((page) => (
        <PageRenderer key={page.id} page={page} />
      ))}
    </RendererContext.Provider>
  )
}
